// @flow
import React from 'react'
import { connect } from 'react-redux'
import { Grid } from '@material-ui/core'
import { head } from 'lodash/array'
import { get } from 'lodash/object'
import i18n from 'i18next'
import queryString from 'query-string'

import { apiServices } from '@modules/api'
import { accountActions } from '@modules/account'
import { routerActions } from '@modules/router'
import { checkoutActions } from '@modules/checkout'
import getMultiLanguagesPaths from '../../master/i18n/translations/multiLanguagesPaths'
import Helmet from '../../master/components/Helmet'
import Navbar from '../../master/components/Navbar'
import Wrapper from '../../master/components/Wrapper'
import { trackPageView } from '../../master/analytics'
import AddAddressForm from '../../account/personal/components/AddAddressForm'
import Steps from '../components/Steps'
import Summary from '../components/Summary'
import AddressPicker from './components/AddressPicker'

const mapStateToProps = (state: Object) => {
  const firstAvailableDeliveryService =
    head(state.checkout.delivery.services) || {}

  return {
    queryStrings: queryString.parse(state.router.location.search),
    addresses: state.account.addresses,
    cart: state.cart,
    customerToken: state.auth.customerToken,
    firstAvailableDeliveryService,
    firstAvailableSlot: get(
      firstAvailableDeliveryService,
      'firstAvailableSlot'
    ),
    hasAddresses: state.account.addresses.length > 0,
    isFetching: apiServices.selectFetching(state, accountActions.FETCH_ACCOUNT),
    isFetchingPostDeliveryAddress: apiServices.selectFetching(
      state,
      checkoutActions.POST_DELIVERY_ADDRESS
    ),
    isFetchingDeliveryServices: apiServices.selectFetching(
      state,
      checkoutActions.FETCH_DELIVERY_SERVICES
    ),
    snackBarItem: state.snackbar.queue[0]
  }
}

export const mapDispatchToProps = (dispatch: Function) => ({
  fetchAccount: () => dispatch(accountActions.fetchAccount()),
  navigateTo: (to: string) => dispatch(routerActions.navigateTo(to)),
  postAddress: (address: Object) =>
    dispatch(accountActions.postAddress(address)),
  postDeliveryAddress: (cartID: number, addressID: number) => {
    return dispatch(checkoutActions.postDeliveryAddress(cartID, addressID))
  },
  postDelivery: (options: Object, route: string) =>
    dispatch(checkoutActions.postDelivery(options, route)),
  updateInvoiceData: (addressID: number, invoiceAddress: Object) =>
    dispatch(accountActions.updateInvoiceData(addressID, invoiceAddress))
})

type Props = {
  addresses: Array<Object>,
  cart: Object,
  customerToken: string,
  fetchAccount: Function,
  fetchCart: Function,
  firstAvailableDeliveryService: Object,
  firstAvailableSlot: Object,
  hasAddresses: boolean,
  isFetching: boolean,
  isFetchingDeliveryServices: boolean,
  isFetchingPostDeliveryAddress: boolean,
  navigateTo: Function,
  postAddress: Function,
  postDelivery: Function,
  postDeliveryAddress: Function,
  queryStrings: Object,
  updateInvoiceData: Function,
  snackBarItem: Object
}

type State = {
  canGoToNextStep: boolean,
  invoiceAddressID: number | null,
  nextStepText: string
}

export class DeliveryContainer extends React.Component<Props, State> {
  state = {
    invoiceAddressID: null,
    canGoToNextStep: false,
    nextStepText: i18n.t('checkout.nextStepPayment')
  }

  componentDidMount () {
    !this.props.cart.products.length &&
      this.props.navigateTo(i18n.t('routes.CHECKOUT_REVIEW'))

    if (window.dataLayer) {
      trackPageView()

      return
    }

    window.onload = () => trackPageView()
  }

  componentDidUpdate (prevProps: Props) {
    if (prevProps.customerToken !== this.props.customerToken) {
      this.props.fetchAccount()
    }

    if (
      prevProps.snackBarItem !== this.props.snackBarItem &&
      this.props.snackBarItem
    ) {
      this.setState({
        canGoToNextStep: true,
        nextStepText: i18n.t('checkout.nextStepPayment')
      })
    }
  }

  setInvoiceAddressID = (invoiceAddressID: number | null) => {
    this.setState({
      invoiceAddressID
    })
  }

  render () {
    const URLComponents = { path: 'routes.CHECKOUT_DELIVERY' }
    return (
      <>
        <Helmet multiLanguagesPaths={getMultiLanguagesPaths(URLComponents)} />
        <Navbar multiLanguagesPaths={getMultiLanguagesPaths(URLComponents)} />
        <Wrapper>
          <Grid container spacing={16}>
            <Steps activeStep={2} />
            <Grid item xs={12} sm={8}>
              {this.props.hasAddresses ? (
                <AddressPicker
                  addresses={this.props.addresses}
                  cartID={this.props.cart.id}
                  firstAvailableDeliveryService={
                    this.props.firstAvailableDeliveryService
                  }
                  isDisabled={this.props.isFetchingDeliveryServices &&
                    this.props.isFetchingPostDeliveryAddress
                  }
                  language={i18n.language}
                  navigateTo={this.props.navigateTo}
                  postDeliveryAddress={this.props.postDeliveryAddress}
                  toggleCanGoToNextStep={() =>
                    this.setState({ canGoToNextStep: true })
                  }
                  setInvoiceAddressID={this.setInvoiceAddressID}
                  updateInvoiceData={this.props.updateInvoiceData}
                  comesFromEditingAddress={
                    this.props.queryStrings.comesFromEditingAddress
                  }
                />
              ) : (
                <AddAddressForm
                  postAddress={this.props.postAddress}
                  isAtCheckout
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Summary
                canGoToNextStep={
                  this.state.canGoToNextStep &&
                  !this.props.isFetchingDeliveryServices
                }
                nextStepText={this.state.nextStepText}
                products={this.props.cart.products}
                showCart
                total={this.props.cart.total}
                totalDiscounts={this.props.cart.order.totalDiscounts}
                totalProducts={this.props.cart.order.totalProducts}
                totalShipping={this.props.cart.order.totalShipping}
                onNextStepClick={() => {
                  this.setState({
                    canGoToNextStep: false,
                    nextStepText: i18n.t('checkout.processingStep')
                  })
                  this.props.postDelivery(
                    {
                      cartID: this.props.cart.id,
                      addressID: this.props.cart.delivery.addressID,
                      deliveryDate: this.props.firstAvailableSlot.day,
                      slotID: head(this.props.firstAvailableSlot.timeSlots).id,
                      invoiceID: this.state.invoiceAddressID
                    },
                    i18n.t('routes.CHECKOUT_PAYMENT')
                  )
                }}
              />
            </Grid>
          </Grid>
        </Wrapper>
      </>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryContainer)
